import React, { useEffect, useState } from 'react'
import { Row, ResponsiveTableRow, Ellipsis, Checkbox } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../../common/DmcResponsiveTable'
import SortableColumnHeader from '../../../common/SortableColumnHeader'
import TableOperations from '../TableOperations'
import useResponsiveTableExpandAll from '../../../../hooks/useResponsiveTableExpandAll'

export default function CompactMessagesListTable({ queueState, compactQueueState, queueActions, compactQueueActions }) {
  const { t } = useTranslation()
  const [rowExpanded, setRowExpanded] = useState({})
  const [allExpanded, setAllExpanded] = useState()

  const toggleExpandAll = () => {
    setRowExpanded(() => {
      const newExpandedState = {}
      queueState.queueContents.forEach((message) => {
        newExpandedState[message.id] = !allExpanded
      })
      return newExpandedState
    })
    setAllExpanded((prev) => {
      const collapsedRaw = localStorage.getItem('queueCollapsed')
      const collapsedObj = collapsedRaw ? JSON.parse(collapsedRaw) : {}
      if (prev) {
        collapsedObj[queueState.queueName] = true
      } else {
        delete collapsedObj[queueState.queueName]
      }
      localStorage.setItem('queueCollapsed', JSON.stringify(collapsedObj))
      return !prev
    })
  }

  useEffect(() => {
    const collapsedRaw = localStorage.getItem('queueCollapsed')
    const collapsedObj = collapsedRaw ? JSON.parse(collapsedRaw) : {}
    setAllExpanded(!collapsedObj[queueState.queueName])
  }, [queueState.queueName])

  useEffect(() => {
    if (allExpanded) {
      setRowExpanded((prev) => {
        const updated = { ...prev }
        queueState.queueContents.forEach((msg) => {
          updated[msg.id] = true
        })
        return updated
      })
    }
  }, [queueState.queueContents, allExpanded])

  useResponsiveTableExpandAll(toggleExpandAll, allExpanded)

  const sortMap = {
    asc: 'ascending',
    desc: 'descending',
  }

  const handleExpandRow = (messageId) => {
    setRowExpanded((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }))
  }

  const memoriesLink = queueState?.queueName?.includes('MEMORIES_ADDED')

  return (
    <DmcResponsiveTable
      id="compactQueueTable"
      totalColumnCount={compactQueueState.activeColumns.columnList.length}
      atSizeColumns={{
        default: {
          labels: compactQueueState.activeColumns.columnList.map((col, index) => (
            <Row key={`header-${col}`} alignY="top">
              {index === 0 && (
                <Checkbox
                  name="selectAll"
                  checked={Object.keys(queueState.selectedCheckboxes).length === queueState.queueContents.length}
                  onChange={queueActions.selectAllCheckboxes}
                />
              )}
              <SortableColumnHeader
                sortField={queueState.sortField}
                sortType={sortMap[queueState.sortDirection]}
                field={col}
                label={t(`queues.${col}`, col)}
                onClick={() => compactQueueActions.sortByFieldType(col)}
              />
            </Row>
          )),
        },
      }}
    >
      {queueState.queueContents.map((message, rIndex) => {
        const columnProps = {}
        compactQueueState.activeColumns.columnList.forEach((key, cIndex) => {
          columnProps[`column${cIndex + 1}`] = (
            <Ellipsis>{compactQueueActions.formatField(message, key, memoriesLink).value}</Ellipsis>
          )
        })
        columnProps.column1 = (
          <Row alignY="top">
            <Checkbox
              onChange={compactQueueActions.handleRowCheckboxChange}
              checked={queueActions.isCheckboxSelected(message)}
              name={rIndex.toString()}
              id={`messageCheckbox${rIndex}`}
            />
            <Ellipsis>
              {compactQueueActions.formatField(message, compactQueueState.activeColumns.columnList[0]).value}
            </Ellipsis>
          </Row>
        )
        return (
          <ResponsiveTableRow
            key={message.id}
            id={`message${rIndex}`}
            value={message.id}
            {...columnProps}
            rowExpanded={rowExpanded[message.id]}
            setRowExpanded={() => handleExpandRow(message.id)}
          >
            {queueState.summaryComponent ? (
              <Row key={message.id}>
                {queueState.summaryComponent(message)}
                <span growX />
                <span noShrinkX>
                  <TableOperations message={message} queueState={queueState} queueActions={queueActions} />
                </span>
              </Row>
            ) : null}
          </ResponsiveTableRow>
        )
      })}
    </DmcResponsiveTable>
  )
}
